<template>
  <!-- One AI module of one tenant. The form is built from the registry schema
       (definition), so new params show up here without frontend changes.
       #11522: params whose labels share a "Prefix: rest" pattern are grouped
       under the prefix, derived from the label text alone. -->
  <v-card outlined class="ai-card ai-module-card">
    <!-- header -->
    <div class="d-flex align-start flex-wrap px-5 pt-5 pb-4">
      <v-avatar size="44" :color="avatarColor" class="mr-4">
        <v-icon color="primary">mdi-robot</v-icon>
      </v-avatar>
      <div class="min-w-0 mr-4">
        <div class="title font-weight-medium">{{ current.label }}</div>
        <div class="caption" :class="mutedClass">
          <span class="module-key">{{ current.module }}</span>
          <template v-if="current.updatedAt">
            · Last saved {{ stamp(current.updatedAt) }}
            <span v-if="current.updatedByName"
              >by {{ current.updatedByName }}</span
            >
          </template>
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-tooltip bottom max-width="320">
        <template v-slot:activator="{ on }">
          <v-chip
            v-on="on"
            small
            outlined
            :color="current.enabled ? 'success' : 'grey'"
            class="mt-2"
          >
            <v-icon small left>
              {{
                current.enabled
                  ? "mdi-check-circle"
                  : "mdi-minus-circle-outline"
              }}
            </v-icon>
            {{ current.enabled ? "Enabled" : "Disabled" }}
          </v-chip>
        </template>
        <span>
          Switched on in the tenant's access settings (AI Modules group,
          ai_modules:{{ current.module }}), not here.
        </span>
      </v-tooltip>
    </div>

    <div v-if="!current.enabled || generalErrors.length" class="px-5 pb-4">
      <v-alert
        v-if="!current.enabled"
        text
        dense
        type="info"
        icon="mdi-information-outline"
        class="body-2 mb-0"
      >
        Not enabled for this tenant, so these settings are not used yet. It is
        switched on in the tenant's access settings,
        <strong>AI Modules</strong> group.
      </v-alert>
      <v-alert
        v-if="generalErrors.length"
        type="error"
        dense
        text
        class="mb-0"
        :class="{ 'mt-3': !current.enabled }"
      >
        <div v-for="(msg, i) in generalErrors" :key="i">{{ msg }}</div>
      </v-alert>
    </div>

    <v-divider></v-divider>

    <!-- Monthly cap -->
    <section class="ai-section">
      <div class="ai-overline mb-3" :class="mutedClass">Monthly cap</div>
      <div class="d-flex align-center param-head">
        <span class="body-2 font-weight-medium">Monthly cap (USD)</span>
      </div>
      <div class="caption param-help" :class="mutedClass">
        Leave empty for no cap. Once the month's spend reaches the cap, further
        calls are skipped.
      </div>
      <div class="d-flex flex-wrap align-center">
        <v-text-field
          v-model="form.cap"
          type="number"
          min="0"
          step="0.01"
          prefix="$"
          placeholder="No cap"
          outlined
          dense
          hide-details="auto"
          class="ai-flush field-number mr-8 my-1"
          :error-messages="fieldErrors.monthlyCapUsd"
          @input="clearError('monthlyCapUsd')"
        ></v-text-field>
        <div class="spend my-1">
          <div class="d-flex align-baseline">
            <span class="title font-weight-medium tabular">{{
              money(current.monthToDateCostUsd)
            }}</span>
            <span class="body-2 ml-2" :class="mutedClass">
              {{
                current.monthlyCapUsd != null
                  ? `of ${money(current.monthlyCapUsd)} this month`
                  : "spent this month · no cap"
              }}
            </span>
            <v-spacer></v-spacer>
            <span
              v-if="current.monthlyCapUsd != null"
              class="caption tabular ml-3"
              :class="`${spendColor}--text`"
            >
              {{ Math.round(spendPercent) }}%
            </span>
          </div>
          <v-progress-linear
            v-if="current.monthlyCapUsd != null"
            :value="spendPercent"
            :color="spendColor"
            height="6"
            rounded
            class="mt-1"
          ></v-progress-linear>
        </div>
      </div>
    </section>

    <!-- Models -->
    <template v-if="form.models.length">
      <v-divider></v-divider>
      <section class="ai-section">
        <div class="ai-overline mb-3" :class="mutedClass">Models</div>
        <v-row dense class="param-grid">
          <v-col v-for="m in form.models" :key="m.kind" cols="12" sm="6" lg="4">
            <div class="d-flex align-center param-head">
              <span class="body-2 font-weight-medium">
                {{ m.kind.toUpperCase() }} model
              </span>
              <v-chip
                v-if="m.input.trim()"
                x-small
                outlined
                color="primary"
                class="ml-2"
              >
                overridden
              </v-chip>
              <v-tooltip v-if="m.input.trim()" bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    icon
                    x-small
                    color="primary"
                    class="ml-1"
                    @click="m.input = ''"
                  >
                    <v-icon small>mdi-restore</v-icon>
                  </v-btn>
                </template>
                <span>Reset to default ({{ m.defaultModel }})</span>
              </v-tooltip>
            </div>
            <v-text-field
              v-model="m.input"
              :placeholder="m.defaultModel"
              :hint="`Default ${m.defaultModel}`"
              persistent-hint
              outlined
              dense
              class="ai-flush field-text"
              :error-messages="fieldErrors[`models.${m.kind}`]"
              @input="clearError(`models.${m.kind}`)"
            ></v-text-field>
          </v-col>
        </v-row>
      </section>
    </template>

    <!-- Params -->
    <v-divider></v-divider>
    <section class="ai-section">
      <div class="ai-overline mb-3" :class="mutedClass">Parameters</div>
      <div v-if="!paramSpecs.length" class="body-2" :class="mutedClass">
        This module has no tunable parameters.
      </div>

      <div
        v-for="(block, bi) in paramBlocks"
        :key="block.key"
        class="param-block"
        :class="{ 'mt-5': bi > 0 }"
      >
        <div v-if="block.title" class="d-flex align-center mb-2">
          <span class="group-bar primary mr-2"></span>
          <span class="subtitle-2">{{ block.title }}</span>
        </div>
        <v-row dense class="param-grid">
          <v-col
            v-for="item in block.items"
            :key="item.spec.key"
            cols="12"
            :sm="item.wide ? 12 : 6"
            :lg="item.wide ? 12 : 4"
          >
            <div class="d-flex align-center param-head">
              <span class="body-2 font-weight-medium">{{ item.label }}</span>
              <v-chip
                v-if="paramOverridden(item.spec)"
                x-small
                outlined
                color="primary"
                class="ml-2"
              >
                overridden
              </v-chip>
              <v-chip
                v-else-if="form.resets.includes(item.spec.key)"
                x-small
                outlined
                class="ml-2"
              >
                reset on save
              </v-chip>
              <v-tooltip v-if="paramOverridden(item.spec)" bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    icon
                    x-small
                    color="primary"
                    class="ml-1"
                    @click="resetParam(item.spec)"
                  >
                    <v-icon small>mdi-restore</v-icon>
                  </v-btn>
                </template>
                <span
                  >Reset to default ({{
                    formatValue(item.spec, defaultOf(item.spec))
                  }})</span
                >
              </v-tooltip>
            </div>
            <div
              v-if="item.spec.help || item.spec.type === 'multi_select'"
              class="caption param-help"
              :class="[mutedClass, { 'param-help--grid': !item.wide }]"
            >
              {{ item.spec.help }}
              <template v-if="item.spec.type === 'multi_select'">
                {{ item.spec.help ? "·" : "" }}
                {{ selectedCount(item.spec) }} of
                {{ (item.spec.options || []).length }} selected · Default:
                {{ formatValue(item.spec, defaultOf(item.spec)) }}
              </template>
            </div>

            <v-text-field
              v-if="
                item.spec.type === 'integer' || item.spec.type === 'decimal'
              "
              v-model="form.params[item.spec.key]"
              type="number"
              :min="item.spec.min != null ? item.spec.min : undefined"
              :max="item.spec.max != null ? item.spec.max : undefined"
              :step="item.spec.type === 'integer' ? 1 : 'any'"
              :suffix="item.unit"
              :hint="numberHint(item.spec)"
              persistent-hint
              outlined
              dense
              class="ai-flush field-number"
              :error-messages="fieldErrors[`params.${item.spec.key}`]"
              @input="onParamInput(item.spec)"
            ></v-text-field>

            <v-switch
              v-else-if="item.spec.type === 'boolean'"
              v-model="form.params[item.spec.key]"
              :label="form.params[item.spec.key] ? 'On' : 'Off'"
              :hint="`Default: ${formatValue(item.spec, defaultOf(item.spec))}`"
              persistent-hint
              inset
              dense
              class="ai-flush mt-1"
              :error-messages="fieldErrors[`params.${item.spec.key}`]"
              @change="onParamInput(item.spec)"
            ></v-switch>

            <div v-else-if="item.spec.type === 'multi_select'">
              <v-row no-gutters class="pt-1">
                <v-col
                  v-for="opt in optionItems(item.spec)"
                  :key="opt.value"
                  cols="12"
                  sm="6"
                  lg="4"
                >
                  <v-checkbox
                    v-model="form.params[item.spec.key]"
                    :value="opt.value"
                    :label="opt.text"
                    class="mt-0 pt-0 option-check"
                    dense
                    hide-details
                    @change="onParamInput(item.spec)"
                  ></v-checkbox>
                </v-col>
              </v-row>
              <div
                v-if="fieldErrors[`params.${item.spec.key}`]"
                class="caption error--text pt-1"
              >
                {{ fieldErrors[`params.${item.spec.key}`].join(", ") }}
              </div>
            </div>

            <v-text-field
              v-else
              v-model="form.params[item.spec.key]"
              :suffix="item.unit"
              :hint="`Default: ${formatValue(item.spec, defaultOf(item.spec))}`"
              persistent-hint
              outlined
              dense
              class="ai-flush field-text"
              :error-messages="fieldErrors[`params.${item.spec.key}`]"
              @input="onParamInput(item.spec)"
            ></v-text-field>
          </v-col>
        </v-row>
      </div>
    </section>

    <v-divider></v-divider>
    <v-card-actions class="px-5 py-3">
      <span v-if="dirty" class="caption d-flex align-center">
        <v-icon x-small color="warning" class="mr-1">mdi-circle</v-icon>
        Unsaved changes
      </span>
      <span v-else class="caption" :class="mutedClass">No unsaved changes</span>
      <v-spacer></v-spacer>
      <v-btn text :disabled="!dirty || saving" @click="init(current)">
        Discard
      </v-btn>
      <v-btn
        color="primary"
        depressed
        :disabled="!dirty"
        :loading="saving"
        @click="save"
      >
        <v-icon small left>mdi-content-save-outline</v-icon>
        Save
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import {
  UPDATE_AI_MODULE_SETTING_MUTATION,
  parseJsonValue,
} from "./ai-setup-graphql";

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// "Margin: tolerance (pp)" -> prefix "Margin", rest "tolerance (pp)".
const PREFIX_RE = /^([^:]{1,40}):\s+(\S.*)$/;
// A short trailing "(km)" becomes the field suffix; longer ones stay in the label.
const UNIT_RE = /^(.*\S)\s*\(([^()]{1,6})\)$/;

const capitalize = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);

const labelParts = (label) => {
  const m = UNIT_RE.exec(label || "");
  return m ? { label: m[1], unit: m[2] } : { label, unit: undefined };
};

export default {
  name: "ai-module-card",

  props: {
    domain: { type: String, required: true },
    // aiModuleRegistry entry: { key, label, models, params: [spec] }
    definition: { type: Object, required: true },
    // aiModuleSettings entry for this tenant + module
    setting: { type: Object, required: true },
  },

  data() {
    return {
      current: this.setting,
      form: { cap: "", models: [], params: {}, resets: [] },
      errors: [],
      saving: false,
    };
  },

  created() {
    this.init(this.setting);
  },

  computed: {
    mutedClass() {
      return this.$vuetify.theme.dark
        ? "grey--text text--lighten-1"
        : "grey--text text--darken-1";
    },
    avatarColor() {
      return this.$vuetify.theme.dark ? "grey darken-3" : "blue lighten-5";
    },
    paramSpecs() {
      return this.definition.params || [];
    },
    // Layout only: params in registry order, split into blocks. Labels sharing
    // a "Prefix: " (2+ params) go in a block titled with the prefix and show
    // only the rest; everything else stays ungrouped. A multi_select gets a
    // full-width block of its own.
    paramBlocks() {
      const parsed = this.paramSpecs.map((spec) => {
        const m = PREFIX_RE.exec(spec.label || "");
        return {
          spec,
          prefix: m ? m[1].trim() : null,
          rest: m ? m[2] : spec.label,
        };
      });
      const counts = {};
      parsed.forEach((p) => {
        if (p.prefix) counts[p.prefix] = (counts[p.prefix] || 0) + 1;
      });
      const blocks = [];
      const titled = {};
      parsed.forEach((p) => {
        const title = p.prefix && counts[p.prefix] > 1 ? p.prefix : null;
        const wide = p.spec.type === "multi_select";
        const item = {
          spec: p.spec,
          wide,
          ...labelParts(
            title ? capitalize(p.rest) : p.spec.label || p.spec.key
          ),
        };
        if (title) {
          if (!titled[title]) {
            titled[title] = { key: `g:${title}`, title, items: [] };
            blocks.push(titled[title]);
          }
          titled[title].items.push(item);
          return;
        }
        const last = blocks[blocks.length - 1];
        if (last && !last.title && !last.wide && !wide) {
          last.items.push(item);
        } else {
          blocks.push({
            key: `u:${p.spec.key}`,
            title: null,
            wide,
            items: [item],
          });
        }
      });
      return blocks;
    },
    // Effective (stored or default) value per param key, parsed.
    storedValues() {
      const map = {};
      (this.current.params || []).forEach((p) => {
        map[p.key] = {
          value: parseJsonValue(p.value),
          overridden: p.overridden,
        };
      });
      return map;
    },
    fieldErrors() {
      const map = {};
      this.errors.forEach((e) => {
        if (!map[e.field]) map[e.field] = [];
        map[e.field].push(e.message);
      });
      return map;
    },
    // Errors with no field on this form (e.g. ai_module) go in the card alert.
    generalErrors() {
      const known = ["monthlyCapUsd"]
        .concat(this.form.models.map((m) => `models.${m.kind}`))
        .concat(this.paramSpecs.map((s) => `params.${s.key}`));
      return this.errors
        .filter((e) => !known.includes(e.field))
        .map((e) => (e.field ? `${e.field}: ${e.message}` : e.message));
    },
    capValue() {
      const raw = this.form.cap == null ? "" : String(this.form.cap).trim();
      return raw === "" ? null : Number(raw);
    },
    capChanged() {
      return this.capValue !== this.current.monthlyCapUsd;
    },
    changedModels() {
      return this.form.models
        .filter((m) => m.input.trim() !== m.original)
        .map((m) => ({ kind: m.kind, model: m.input.trim() }));
    },
    changedParams() {
      const out = [];
      this.paramSpecs.forEach((spec) => {
        if (this.form.resets.includes(spec.key)) {
          out.push({ key: spec.key, value: null });
          return;
        }
        const value = this.fromInput(spec, this.form.params[spec.key]);
        const stored = this.storedValues[spec.key];
        const original = stored ? stored.value : this.defaultOf(spec);
        if (!same(value, original)) {
          out.push({ key: spec.key, value: JSON.stringify(value) });
        }
      });
      return out;
    },
    dirty() {
      return (
        this.capChanged ||
        this.changedModels.length > 0 ||
        this.changedParams.length > 0
      );
    },
    spendPercent() {
      const cap = this.current.monthlyCapUsd;
      if (!cap) return this.current.monthToDateCostUsd > 0 ? 100 : 0;
      return Math.min(100, (this.current.monthToDateCostUsd / cap) * 100);
    },
    spendColor() {
      if (this.spendPercent >= 100) return "error";
      if (this.spendPercent >= 80) return "warning";
      return "primary";
    },
  },

  methods: {
    money,
    selectedCount(spec) {
      const v = this.form.params[spec.key];
      return Array.isArray(v) ? v.length : 0;
    },
    init(setting) {
      this.current = setting;
      this.errors = [];
      const params = {};
      this.paramSpecs.forEach((spec) => {
        const stored = this.storedValues[spec.key];
        params[spec.key] = this.toInput(
          spec,
          stored ? stored.value : this.defaultOf(spec)
        );
      });
      this.form = {
        cap: setting.monthlyCapUsd != null ? String(setting.monthlyCapUsd) : "",
        models: (setting.models || []).map((m) => {
          const original = m.overridden ? m.model || "" : "";
          return {
            kind: m.kind,
            defaultModel: m.defaultModel,
            original,
            input: original,
          };
        }),
        params,
        resets: [],
      };
    },
    defaultOf(spec) {
      return parseJsonValue(spec.default);
    },
    toInput(spec, value) {
      switch (spec.type) {
        case "integer":
        case "decimal":
          return value == null ? "" : String(value);
        case "boolean":
          return !!value;
        case "multi_select":
          return Array.isArray(value) ? value.slice() : [];
        default:
          return value == null ? "" : String(value);
      }
    },
    fromInput(spec, input) {
      switch (spec.type) {
        case "integer":
        case "decimal": {
          const raw = input == null ? "" : String(input).trim();
          return raw === "" ? null : Number(raw);
        }
        case "boolean":
          return !!input;
        case "multi_select": {
          const chosen = Array.isArray(input) ? input : [];
          // backend stores the subset in options order
          return (spec.options || []).filter((o) => chosen.includes(o));
        }
        default:
          return input == null ? "" : String(input);
      }
    },
    // Same rule as the backend's overridden flag (value != default), applied
    // to what is in the form, so it follows unsaved edits too.
    paramOverridden(spec) {
      if (this.form.resets.includes(spec.key)) return false;
      return !same(
        this.fromInput(spec, this.form.params[spec.key]),
        this.defaultOf(spec)
      );
    },
    resetParam(spec) {
      this.form.params[spec.key] = this.toInput(spec, this.defaultOf(spec));
      const stored = this.storedValues[spec.key];
      if (stored && stored.overridden && !this.form.resets.includes(spec.key)) {
        this.form.resets.push(spec.key);
      }
      this.clearError(`params.${spec.key}`);
    },
    onParamInput(spec) {
      const i = this.form.resets.indexOf(spec.key);
      if (i >= 0) this.form.resets.splice(i, 1);
      this.clearError(`params.${spec.key}`);
    },
    clearError(field) {
      this.errors = this.errors.filter((e) => e.field !== field);
    },
    // optionLabels runs parallel to options; fall back to the raw option.
    optionItems(spec) {
      const labels = spec.optionLabels || [];
      return (spec.options || []).map((o, i) => ({
        text: labels[i] || o,
        value: o,
      }));
    },
    formatValue(spec, value) {
      if (spec.type === "boolean") return value ? "On" : "Off";
      if (spec.type === "multi_select") {
        if (!Array.isArray(value) || !value.length) return "none";
        const options = spec.options || [];
        if (options.length && options.every((o) => value.includes(o))) {
          return "all";
        }
        const items = this.optionItems(spec);
        return value
          .map((v) => {
            const item = items.find((it) => it.value === v);
            return item ? item.text : v;
          })
          .join(", ");
      }
      return value == null || value === "" ? "empty" : String(value);
    },
    numberHint(spec) {
      const parts = [`Default ${this.formatValue(spec, this.defaultOf(spec))}`];
      if (spec.min != null && spec.max != null) {
        parts.push(`${spec.min}–${spec.max}`);
      } else if (spec.min != null) {
        parts.push(`min ${spec.min}`);
      } else if (spec.max != null) {
        parts.push(`max ${spec.max}`);
      }
      if (spec.type === "integer") parts.push("whole number");
      return parts.join(" · ");
    },
    stamp(iso) {
      const d = new Date(iso);
      return isNaN(d.getTime()) ? iso : d.toLocaleString();
    },
    save() {
      if (
        this.capValue !== null &&
        (isNaN(this.capValue) || this.capValue < 0)
      ) {
        this.errors = [
          { field: "monthlyCapUsd", message: "must be a number of 0 or more" },
        ];
        return;
      }
      // Only what changed is sent: an omitted argument keeps the stored value,
      // whereas monthlyCapUsd: null would clear the cap.
      const variables = { domain: this.domain, module: this.current.module };
      if (this.capChanged) variables.monthlyCapUsd = this.capValue;
      if (this.changedModels.length) variables.models = this.changedModels;
      if (this.changedParams.length) variables.params = this.changedParams;

      this.saving = true;
      this.errors = [];
      this.$apollo
        .mutate({ mutation: UPDATE_AI_MODULE_SETTING_MUTATION, variables })
        .then(({ data }) => {
          const r = data.updateAiModuleSetting;
          if (!r.ok) {
            this.errors = r.errors || [];
            this.$store.dispatch("showAppSnackbar", {
              msg: "AI Setup not saved, please check the highlighted fields.",
              color: "error",
            });
            return;
          }
          this.init(r.setting);
          this.$store.dispatch("showAppSnackbar", {
            msg: `${this.current.label} settings saved.`,
          });
        })
        .catch((err) => {
          const msg =
            err && err.graphQLErrors && err.graphQLErrors.length
              ? err.graphQLErrors.map((e) => e.message).join(", ")
              : (err && err.message) || "Save failed.";
          this.$store.dispatch("showAppSnackbar", { msg, color: "error" });
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style scoped>
.min-w-0 {
  min-width: 0;
}
.module-key {
  font-family: "Roboto Mono", monospace;
}
.ai-section {
  padding: 20px;
}
.param-head {
  min-height: 24px;
}
.param-help {
  line-height: 1.4;
  margin-bottom: 6px;
}
/* two lines reserved so fields in one grid row line up */
.param-help--grid {
  min-height: 2.8em;
}
.group-bar {
  display: inline-block;
  width: 3px;
  height: 16px;
  border-radius: 2px;
}
.field-number {
  max-width: 200px;
}
.field-text {
  max-width: 320px;
}
.spend {
  min-width: 240px;
  max-width: 340px;
  flex: 1 1 240px;
}
.param-grid > .col {
  padding-bottom: 12px;
}
.option-check >>> .v-label {
  font-size: 0.875rem;
}
.option-check {
  margin-bottom: 2px;
}
</style>



// WEBPACK FOOTER //
// src/components/gt/admin/ai-setup/AiModuleCard.vue